'use client';

/** @jsxImportSource @emotion/react */

import { useEffect, useRef } from 'react';

import {
  Container,
  FlexItem,
  FlexLayout,
  Typography,
  useBreakPoints,
  VideoPlayer,
} from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useTranslations } from 'next-intl';

import { ANALYTICS_EVENTS, trackEvent } from '@/analytics';
import { useThemeMode } from '@/context';

import { useStyles } from './DigitalProduct.styles';
import { getVideoAnalyticsData, VIDEO_URL } from './DigitalProduct.utils';

const DigitalProduct = () => {
  const { isTablet, isDesktop } = useBreakPoints();
  const { isDark } = useThemeMode();

  const videoWrapperRef = useRef<HTMLDivElement>(null);

  const desktopImg = '/images/dsasvideoPosterDesktop.png';
  const tabletImg = '/images/dsasvideoPosterTablet.png';
  const mobileImg = '/images/dsasvideoPosterMobile.png';

  const posterImgSrc = isDesktop ? desktopImg : isTablet ? tabletImg : mobileImg;

  const styles = useStyles();
  const t = useTranslations('DigitalProduct');

  useEffect(() => {
    const wrapper = videoWrapperRef.current;

    if (!wrapper) return;

    const video = wrapper.querySelector('video');

    if (!video) return;

    const handlePlay = () => {
      trackEvent(ANALYTICS_EVENTS.VIDEO_PLAYED, getVideoAnalyticsData(video));
    };

    const handlePause = () => {
      // The browser also fires "pause" when the video naturally ends.
      // In that case, only VIDEO_COMPLETED should be tracked.
      if (video.ended) return;

      trackEvent(ANALYTICS_EVENTS.VIDEO_PAUSED, getVideoAnalyticsData(video));
    };

    const handleEnded = () => {
      trackEvent(ANALYTICS_EVENTS.VIDEO_COMPLETED, getVideoAnalyticsData(video));
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <Container as="section" css={styles.wrapper} id="digital-Product">
      <FlexLayout
        direction="column"
        alignItems="center"
        justifyContent="center"
        padding={{ default: 0 }}
      >
        <FlexItem css={styles.sectionWrapper}>
          <FlexLayout direction="column" gap={{ default: 12, md: 16 }} padding={{ default: 0 }}>
            <FlexItem>
              <Typography variant="h2" css={styles.sectionTitle}>
                {t('title')}
              </Typography>
            </FlexItem>

            <FlexItem css={styles.subtitleWrapper}>
              <Typography variant="p" css={styles.subtitle}>
                {t('subtitle')}
              </Typography>
            </FlexItem>
          </FlexLayout>
        </FlexItem>

        <FlexItem css={styles.videoFrame}>
          <div ref={videoWrapperRef}>
            <VideoPlayer
              posterImgSrc={posterImgSrc}
              controls
              variant={isDark ? 'secondary' : 'primary'}
              captions={[
                {
                  label: 'English',
                  lang: 'en',
                  src: '/caption/captions-dsaas-en.vtt',
                  mobileSrc: '/caption/captions-dsaas-mobile-en.vtt',
                },
              ]}
              src={VIDEO_URL}
              customStyles={{
                container: styles.videoPlayerContainer,
                controls: {
                  popOver: {
                    listContentWrapper: styles.listContentWrapper,
                    listLabel: styles.listLabel,
                  },
                },
                centerPlayPauseIcon: {
                  centerPlayPauseIconWrapper: css`
                    background-color: ${
                      isDark ? 'rgba(23, 206, 202, 0.55)' : 'rgba(64, 84, 209, 0.55)'
                    };
                  `,
                  playPauseIcon: css`
                    color: #fff;
                  `,
                },
              }}
            />
          </div>
        </FlexItem>
      </FlexLayout>
    </Container>
  );
};

export default DigitalProduct;
